import { useState } from "react";
import { Box, Button, useTheme, useMediaQuery } from "@mui/material";
import { CaseWhenBenefit } from "services/GraphQl";
import moment from "moment";
import { BenefitCard } from "./MonthlyBenefitCard";

interface WhenBenefitTrackerProps {
  whenBenefit?: CaseWhenBenefit;
  setModalOpen: (open: boolean) => void;
}

const WhenBenefitMonthlyTracker = ({ whenBenefit, setModalOpen }: WhenBenefitTrackerProps) => {
  const [startIndex, setStartIndex] = useState(0);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  // md and up
  let cardsPerPage = 3;
  if (isXs) cardsPerPage = 1;
  else if (isSm) cardsPerPage = 2;

  const cardWidth = 360;
  const containerWidth = cardsPerPage * cardWidth;

  const months = whenBenefit?.months || [];
  const monthCards = months.map((m) => {
    const monthMoment = moment(m.month, "YYYY-MM");
    const deadlineMoment = moment(m.deadline);
    return {
      month: monthMoment.format("MMMM"),
      year: monthMoment.format("YYYY"),
      expireDate: deadlineMoment,
      total: m.amount ?? 0,
      pending: m.requested ?? 0,
      reimbursed: m.reimbursedAmount ?? 0,
      remaining: (m.amount ?? 0) - (m.requested ?? 0) - (m.reimbursedAmount ?? 0),
    };
  });

  const maxStartIndex = Math.max(0, monthCards.length - cardsPerPage);
  const numDots = maxStartIndex + 1;

  const handleArrow = (dir: -1 | 1) => {
    setStartIndex((prev) => Math.max(0, Math.min(maxStartIndex, prev + dir)));
  };

  const handleDot = (dotIndex: number) => {
    setStartIndex(dotIndex);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          overflow: "hidden",
          position: "relative",
          mx: "auto",
          width: containerWidth,
        }}
      >
        <Box
          sx={{
            display: "flex",
            transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: `translateX(-${startIndex * cardWidth}px)`,
            width: monthCards.length * cardWidth,
          }}
        >
          {monthCards.map((card, index) => (
            <Box
              key={index}
              sx={{
                width: cardWidth,
                flex: "0 0 auto",
                px: 1.5,
              }}
            >
              <BenefitCard {...card} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Carousel Arrows and Dots */}
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 4, mb: 4, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => handleArrow(-1)}
          disabled={startIndex === 0}
          sx={{ minWidth: 36, px: 0 }}
        >
          &lt;
        </Button>
        {Array.from({ length: numDots }).map((_, dotIndex) => (
          <Box
            key={dotIndex}
            onClick={() => handleDot(dotIndex)}
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: dotIndex === startIndex ? "#007c60" : "#e0e0e0",
              mx: 0.75,
              cursor: "pointer",
              border: dotIndex === startIndex ? "2px solid #007c60" : "none",
              transition: "background 0.2s, border 0.2s",
            }}
          />
        ))}
        <Button
          variant="outlined"
          onClick={() => handleArrow(1)}
          disabled={startIndex === maxStartIndex || numDots === 0}
          sx={{ minWidth: 36, px: 0 }}
        >
          &gt;
        </Button>
      </Box>

      {/* Make a Claim Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Button
          variant="contained"
          onClick={() => setModalOpen(true)}
          sx={{
            bgcolor: "#007c60",
            color: "white",
            "&:hover": {
              bgcolor: "#005a45",
            },
            borderRadius: "20px",
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: "bold",
          }}
        >
          Make A Claim
        </Button>
      </Box>
    </Box>
  );
};

export default WhenBenefitMonthlyTracker;
